import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useSiteContent } from "@/context/SiteContentContext";

export function BusinessSection() {
  const [tiers, setTiers] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { api.businessTiers().then(setTiers).catch(() => {}); }, []);

  return (
    <section id="business" className="bg-paper py-20 sm:py-28 px-5 sm:px-10" data-testid="business-section">
      <div className="max-w-[1500px] mx-auto">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-end mb-10">
          <div>
            <span className="hr-overline text-saddle">HomeRange for Business</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-forest mt-2 leading-none tracking-tight">Put your brand, hunt or service in front of the right hunters.</h2>
            <p className="text-charcoal/60 mt-4 max-w-2xl">Self-service storefronts, hunt listings, advertising, analytics and AI-readable structured listings for brands, outfitters, guides and landowners.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <button onClick={() => navigate("/advertise")} className="bg-forest text-bone px-6 py-3.5 font-semibold hover:bg-forest-2 transition-colors" data-testid="business-list">List Your Business</button>
            <button onClick={() => navigate("/advertise")} className="border border-forest/40 text-forest px-6 py-3.5 font-semibold hover:bg-forest hover:text-bone transition-colors" data-testid="business-advertise">Advertise</button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {tiers.map((t, i) => (
            <button key={t.id} onClick={() => navigate("/business")} className={`text-left border p-7 transition-transform hover:-translate-y-1 ${i === 1 ? "bg-forest text-bone border-forest" : "bg-white border-saddle/20 text-charcoal"}`} data-testid={`business-tier-${t.id}`}>
              <span className="hr-overline text-saddle">{t.name}</span>
              <div className="font-serif text-4xl mt-3">
                {t.monthly_price === 0 ? "Free" : `$${t.monthly_price}`}
                {t.monthly_price !== 0 && <span className="text-sm font-sans opacity-60">/mo</span>}
              </div>
              <ul className="mt-5 space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check size={16} className="text-saddle mt-0.5 shrink-0" /> <span className={i === 1 ? "text-bone/85" : "text-charcoal/70"}>{f}</span>
                  </li>
                ))}
              </ul>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  const { footer, footer_links } = useSiteContent();
  const cols = Array.isArray(footer_links) ? footer_links.filter((c) => c.title && (c.links || []).length) : [];
  const isInternal = (h) => h && h.startsWith("/");
  return (
    <footer className="relative bg-forest text-bone/70 px-5 sm:px-10 py-20 grain" data-testid="site-footer">
      <div className="relative z-10 max-w-[1500px] mx-auto">
        <div className="flex flex-col items-center text-center gap-5 pb-12 border-b border-bone/10">
          <img src="/brand/badge.png" alt="HomeRange — Est. 2026 · North American Hunting" className="w-32 h-32 object-contain" data-testid="footer-badge" />
          <p className="font-serif text-2xl sm:text-3xl text-bone tracking-tight" data-testid="footer-tagline">{footer.tagline}</p>
          <p className="hr-overline text-saddle">Where Hunters Belong · Est. 2026</p>
        </div>
        {cols.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-8 py-10 border-b border-bone/10" data-testid="footer-link-columns">
            {cols.map((c) => (
              <div key={c.title} data-testid={`footer-col-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <h4 className="hr-overline text-saddle mb-3">{c.title}</h4>
                <ul className="space-y-2">
                  {c.links.map((l) => (
                    <li key={l.label}>
                      {isInternal(l.href)
                        ? <Link to={l.href} className="text-sm text-bone/60 hover:text-blaze transition-colors">{l.label}</Link>
                        : <a href={l.href || "#"} className="text-sm text-bone/60 hover:text-blaze transition-colors">{l.label}</a>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-8">
          <Logo dark />
          <p className="font-serif text-lg text-bone" data-testid="footer-motto">{footer.motto}</p>
          <small className="text-bone/40 max-w-xs sm:text-right">HomeRange · Public browsing + account personalization + transparent value engine.</small>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-8 mt-8 border-t border-bone/10 text-sm">
          <small className="text-bone/40">© {new Date().getFullYear()} HomeRange. All rights reserved.</small>
          <nav className="flex items-center gap-5">
            <Link to="/partner/onboarding" className="text-bone/60 hover:text-blaze transition-colors font-semibold" data-testid="footer-become-partner">Become a Partner</Link>
            <Link to="/privacy" className="text-bone/60 hover:text-blaze transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="text-bone/60 hover:text-blaze transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
